import { makeAutoObservable, runInAction } from 'mobx'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserAlertService from './../../../shared/services/UserAlert.service'
import PushMessageService from './../../../shared/services/PushMessage.service'
import UserAlertsStore from './../../users/UserAlertsIndicator/UserAlerts.store'

const ALERT_KEY = 'admin.push-notification'

/**
 * Backs the persistent banner for unacknowledged CRITICAL messages.
 *
 * Severity lives on `push_messages`, but the acknowledged state lives on
 * `user_alerts` — so this pairs the two. Alerts come from UserAlertsStore,
 * which the bell already fetches on every page, so no extra alerts request.
 */
class AgentPushBannerStore {
  constructor() {
    makeAutoObservable(this)
  }

  /** push_messages.id -> row */
  messages = {}
  isLoaded = false
  isLoading = false
  _loadedForUserId = null
  /** reference_ids a fetch has already covered, found or not. */
  _requestedRefIds = {}

  /** Unacknowledged BST message alerts, from the bell's existing fetch. */
  get pendingAlerts() {
    return (UserAlertsStore.get() || []).filter(a => a && a.alert_key === ALERT_KEY && !a.acknowledged_at)
  }

  /** Of those, the ones the sender marked critical. */
  get criticalAlerts() {
    if (!this.isLoaded) return []
    return this.pendingAlerts.filter(a => {
      const message = this.messages[`${a.reference_id}`]
      return message && `${message.severity}` === 'critical'
    })
  }

  /**
   * A pending alert whose message no fetch has covered yet — e.g. one that
   * arrived after the first load in this session.
   */
  get hasUnresolved() {
    return this.pendingAlerts.some(a => !this._requestedRefIds[`${a.reference_id}`])
  }

  messageFor = alert => (alert ? this.messages[`${alert.reference_id}`] : null)

  /**
   * Fetch severities. Called from the component whenever there are pending
   * alerts we have not resolved yet.
   */
  load = async () => {
    const userId = UserProfileService.getUserId()
    if (!userId) return
    if (this.isLoading) return
    if (this._loadedForUserId !== userId) this.reset()
    if (!this.hasUnresolved) return

    const refIds = this.pendingAlerts.map(a => `${a.reference_id}`)
    runInAction(() => {
      this.isLoading = true
    })
    try {
      const result = await PushMessageService.search({
        search: { user_id: userId },
        order_by: { created_at: 'DESC' },
        pagination: false,
      })
      const models = (result && result.models) || (Array.isArray(result) ? result : [])
      const messages = {}
      models.forEach(m => {
        messages[`${m.id}`] = m
      })
      runInAction(() => {
        this.messages = messages
        this.isLoaded = true
        this._loadedForUserId = userId
        refIds.forEach(id => {
          this._requestedRefIds[id] = true
        })
      })
    } catch (ex) {
      // Non-fatal. Without severities we simply show no banner — the message
      // still reaches the agent via the bell and the alerts page.
      console.error('[push-banner] load failed', ex)
      runInAction(() => {
        this.isLoaded = true
        this._loadedForUserId = userId
        refIds.forEach(id => {
          this._requestedRefIds[id] = true
        })
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /** Dismissing IS acknowledging — there is no "hide but stay unread". */
  acknowledge = async alert => {
    if (!alert || !alert.id) return
    try {
      const res = await UserAlertService.acknowledged(alert.id)
      if (res && res.id) {
        // Re-fetch so the bell badge and this banner agree.
        await UserAlertsStore.fetch(UserProfileService.getUserId())
        UserAlertsStore.setNumberOfAlerts(UserAlertsStore.count('unacknowledged'))
      }
    } catch (ex) {
      console.error('[push-banner] acknowledge failed', ex)
    }
  }

  reset = () => {
    runInAction(() => {
      this.messages = {}
      this.isLoaded = false
      this._loadedForUserId = null
      this._requestedRefIds = {}
    })
  }
}

export default new AgentPushBannerStore()
